import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { ErrorPage } from '@/components/common/ErrorPage';
import { Label, LABEL_TYPE, ToggleSwitch } from '@/components/generic';
import {
  BUTTON_TYPE,
  ButtonConfig,
  ButtonVariant
} from '@/components/generic/Button/button-config';
import { IconButtonVariant } from '@/components/generic/IconButton/icon-button-config';
import { DetailsPageLayout } from '@/components/layouts/DetailsPageLayout';
import { ConfirmationModal } from '@/components/modals/ConfirmationModal';
import { queryClient } from '@/configuration/react-query';
import { PERMISSION_TYPE, PRIVATE_PAGE_ROUTES } from '@/enums';
import { showToast, ToastType } from '@/helpers/toast';
import { EditPenIcon, PaymentPlanIcon, TrashIcon } from '@/resources';
import { getAllOrganizationPermissions } from '@/services/organizationPermissions.service';
import {
  useDeletePaymentPlanMutation,
  useGetPaymentPlanById,
  useSetDefaultPaymentPlanMutation
} from '@/services/paymentPlan.service';
import { OrganizationPermission } from '@/types';

export const PaymentPlanDetails = () => {
  const [t] = useTranslation('common');
  const { planId } = useParams();
  const navigate = useNavigate();
  const [error, setError] = useState<number>();
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState<boolean>(false);

  const { data: paymentPlanData, isLoading } = useGetPaymentPlanById(Number(planId), {
    onSuccess: () => undefined,
    onError: (error) => {
      setError(error.status);
    }
  });

  const { data: response } = getAllOrganizationPermissions(undefined, 'Id', true);

  const organizationPermissions = (response?.data?.items as OrganizationPermission[]) || [];

  const middleIndex = Math.ceil(organizationPermissions.length / 2);
  const firstHalf = organizationPermissions.slice(0, middleIndex);
  const secondHalf = organizationPermissions.slice(middleIndex);

  const { mutate: deletePaymentPlan } = useDeletePaymentPlanMutation(queryClient, {
    onSuccess: () => {
      navigate(PRIVATE_PAGE_ROUTES.PAYMENT_PLANS);
      setIsOpenDeleteModal(false);
    },
    onError: () => {
      setIsOpenDeleteModal(false);
    }
  });

  const { mutate: setDefaultPaymentPlan, isLoading: isSettingDefault } =
    useSetDefaultPaymentPlanMutation(queryClient, {
      onSuccess: () => {
        showToast(ToastType.SUCCESS, t('paymentPlanSuccesfullyUpdated'));
      },
      onError: (error) => {
        const message =
          error.response?.data?.ErrorMessages?.[0]?.DisplayMessage ?? t('somethingWentWrong');
        showToast(ToastType.ERROR, message);
      }
    });

  const handleEditPlan = () => {
    const editPlanUrl = PRIVATE_PAGE_ROUTES.EDIT_PAYMENT_PLAN.replace(':planId', planId ?? '');
    navigate(editPlanUrl);
  };

  const handleDelete = () => {
    setIsOpenDeleteModal(true);
  };

  const onCloseDeleteModal = () => {
    setIsOpenDeleteModal(false);
  };

  const handleSetAsDefault = () => {
    if (!planId) {
      return;
    }

    setDefaultPaymentPlan(Number(planId));
  };

  const headerButtonsConfig: ButtonConfig[] = [
    {
      label: t('setToDefault'),
      variant: ButtonVariant.PRIMARY,
      type: BUTTON_TYPE.BUTTON,
      handleOnClick: handleSetAsDefault,
      permissions: [PERMISSION_TYPE.UPDATE_PAYMENT_PLAN],
      isDisabled: isSettingDefault,
      isHidden: !!paymentPlanData?.data?.isDefault
    },
    {
      icon: <EditPenIcon className='text-primary-blue' />,
      variant: IconButtonVariant.PRIMARY,
      type: BUTTON_TYPE.ICON_BUTTON,
      handleOnClick: handleEditPlan,
      permissions: [PERMISSION_TYPE.UPDATE_PAYMENT_PLAN]
    },
    {
      icon: <TrashIcon className='h-4 w-3 md:h-5 md:w-4' />,
      variant: IconButtonVariant.PRIMARY,
      type: BUTTON_TYPE.ICON_BUTTON,
      handleOnClick: handleDelete,
      permissions: [PERMISSION_TYPE.DELETE_PAYMENT_PLAN]
    }
  ];

  return error ? (
    <ErrorPage code={error} />
  ) : (
    <div>
      <DetailsPageLayout
        icon={<PaymentPlanIcon className='stroke-[0.5] text-lg text-primary-blue' />}
        path={[{ label: t('planDetails') }]}
        iconPath={PRIVATE_PAGE_ROUTES.PAYMENT_PLANS}
        title={`${paymentPlanData?.data.name}`}
        tag={paymentPlanData?.data.isDefault ? 'Default' : ''}
        isLoading={isLoading}
        headerButtons={headerButtonsConfig}>
        <div className='mt-3 flex space-x-9 md:mt-12'>
          <div className='flex w-full flex-col gap-4'>
            <div className='flex w-full flex-col md:flex-row md:items-center'>
              <Label
                text={`${t('paymentPlanName')}:`}
                type={LABEL_TYPE.PARAGRAPH1}
                className='pb-2.5 text-secondary-gray sm:p-0 md:w-1/6'
              />
              <Label
                text={paymentPlanData?.data?.name}
                type={LABEL_TYPE.PARAGRAPH1}
                className='!text-primary-black'
              />
            </div>
            <div className='w-full'>
              <Label
                className='text-secondary-gray'
                text={`${t('description')}:`}
                type={LABEL_TYPE.PARAGRAPH1}
              />
            </div>
            <div className='w-full'>
              <Label
                text={`${paymentPlanData?.data.description ? paymentPlanData?.data.description : ''}`}
                type={LABEL_TYPE.PARAGRAPH1}
                className='justify-start !text-primary-black'
              />
            </div>
            <div className='mt-4 md:mt-6'>
              <Label text={t('services')} type={LABEL_TYPE.PARAGRAPH2} />
              <div className='flex w-full flex-col md:flex-row'>
                <div className='flex flex-col md:w-full'>
                  {firstHalf.map((permission: OrganizationPermission) => (
                    <div
                      key={permission.id}
                      className='mt-6 flex w-full flex-row justify-between md:pr-8'>
                      <Label text={t(`${permission.name}`)} type={LABEL_TYPE.PARAGRAPH2} />
                      <ToggleSwitch
                        toggled={paymentPlanData?.data.organizationPermissions
                          .map((op: OrganizationPermission) => op.id)
                          .includes(permission.id)}
                      />
                    </div>
                  ))}
                </div>
                <div className='mt-6 hidden w-[3px] bg-secondary-blue-gray md:flex'></div>
                <div className='flex flex-col md:w-full'>
                  {secondHalf.map((permission: OrganizationPermission) => (
                    <div
                      key={permission.id}
                      className='mt-6 flex w-full flex-row justify-between md:pl-8'>
                      <Label text={t(`${permission.name}`)} type={LABEL_TYPE.PARAGRAPH2} />
                      <ToggleSwitch
                        toggled={paymentPlanData?.data.organizationPermissions
                          .map((op: OrganizationPermission) => op.id)
                          .includes(permission.id)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </DetailsPageLayout>
      {isOpenDeleteModal && (
        <ConfirmationModal
          onConfirm={() => deletePaymentPlan(paymentPlanData?.data?.id)}
          isOpen={isOpenDeleteModal}
          onClose={onCloseDeleteModal}
          message={t('deleteMessage', { item: paymentPlanData?.data.name })}
        />
      )}
    </div>
  );
};
